import React, { type ReactNode, useRef } from 'react';
import {
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldAttributes } from '../../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { type MlaCityProps } from '../../../fields/mla-city/mla-city/mla-city-props';
import { useAddressLabel } from '../../../hooks/use-address-label';
import {
  findChildOfType,
  applyInternalProps,
} from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error } from '../../field-error/field-error-headless';
import {
  Label,
  type LabelInternalProps,
} from '../../field-label/field-label-headless';

export type CityProps = {
  children: ReactNode;
};

export const City = ({ children }: CityProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
  } = useFieldPropsV2<MlaCityProps>();

  const cityLabel = useAddressLabel('city');
  const labelElement = findChildOfType(children, City.Label);
  const inputElement = findChildOfType(children, City.Input);
  const descriptionElement = findChildOfType(children, City.Description);
  const errorElement = findChildOfType(children, City.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() =>
        applyInternalProps<LabelInternalProps>(labelElement, {
          label: cityLabel,
        })
      }
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { id } = useFieldPropsV2<MlaCityProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
    />
  );
};

City.Label = Label;
City.Input = Input;
City.Description = Description;
City.Error = Error;
